// ============================================================
// LinuxIntro.jsx
//
// A branded, responsive course content page for the
// "Introduction to Linux" lesson.
//
// Brand palette:
//   Background  → #100720  (deep purple-black)
//   Primary     → goldenrod / #DAA520
//   Accent      → #FFD700  (gold highlights)
//   Text        → #FFFFFF  (white)
//   Surface     → rgba(255,255,255,0.04) with gold borders
//
// Built with:
//   - Material UI (MUI v5) for layout, typography & icons
//   - Fully responsive (xs → xl)
//   - Commented throughout for junior developers
// ============================================================

import React, { useState } from "react";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import TerminalIcon          from "@mui/icons-material/Terminal";
import MenuBookIcon          from "@mui/icons-material/MenuBook";
import LightbulbIcon         from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LinkIcon              from "@mui/icons-material/Link";
import ExpandMoreIcon        from "@mui/icons-material/ExpandMore";
import ExpandLessIcon        from "@mui/icons-material/ExpandLess";
import ContentCopyIcon       from "@mui/icons-material/ContentCopy";
import DoneIcon              from "@mui/icons-material/Done";
import StorageIcon           from "@mui/icons-material/Storage";
import FolderOpenIcon        from "@mui/icons-material/FolderOpen";
import BuildIcon             from "@mui/icons-material/Build";
import LayersIcon            from "@mui/icons-material/Layers";
import CodeIcon              from "@mui/icons-material/Code";
import SchoolIcon            from "@mui/icons-material/School";
import StarIcon              from "@mui/icons-material/Star";

// ── Brand tokens ──────────────────────────────────────────
// Centralise all colours here so a junior dev can change
// the whole theme by editing just this one object.
const BRAND = {
    bg:          "#233D4D",          // page background
    surface:     "rgba(255,255,255,0.04)", // card fill
    border:      "rgba(218,165,32,0.25)",  // goldenrod @ 25% opacity
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",          // goldenrod
    goldBright:  "#FFD700",          // bright gold for highlights
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

// ── Reusable section-heading style ────────────────────────
// A small helper object so every section title looks the same.
const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ============================================================
//  SUB-COMPONENT: SectionTitle
//  Renders a gold-accented heading with an MUI icon.
//  Props:
//    icon  – MUI icon element
//    label – heading text
// ============================================================
function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            {/* Coloured icon circle */}
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: BRAND.goldGlow,
                    border: `1px solid ${BRAND.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {/* Clone the passed icon and apply the brand gold colour */}
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>

            {/* Heading text */}
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ConceptCard
//  A single concept card with icon, title, and description.
//  Props:
//    icon        – MUI icon element
//    title       – short concept name
//    description – 1-2 sentence explanation
// ============================================================
function ConceptCard({ icon, title, description }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                height: "100%",                      // fill Grid cell
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 3,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 20px ${BRAND.goldGlow}`,
                },
            }}
        >
            {/* Icon row */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 22 } })}
                <Typography
                    sx={{
                        fontFamily: "'Georgia', serif",
                        fontWeight: 700,
                        color: BRAND.white,
                        fontSize: 14,
                    }}
                >
                    {title}
                </Typography>
            </Box>

            {/* Description */}
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.65 }}>
                {description}
            </Typography>
        </Paper>
    );
}

// ============================================================
//  SUB-COMPONENT: CodeBlock
//  Displays one or more terminal commands with a copy button.
//  Props:
//    lines – array of { cmd, comment } objects
// ============================================================
function CodeBlock({ lines }) {
    // Track which block was recently copied so we can show a tick
    const [copied, setCopied] = useState(false);

    // Build the full text string from all command lines
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            // Reset the icon back to "copy" after 2 seconds
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box
            sx={{
                background: BRAND.codeBg,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                overflow: "hidden",
                mb: 2,
            }}
        >
            {/* ── Top bar: mimics a terminal title bar ── */}
            <Box
                sx={{
                    px: 2,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: `1px solid ${BRAND.border}`,
                    background: "rgba(0,0,0,0.3)",
                }}
            >
                {/* Terminal dots (decorative) */}
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>

                {/* Copy button */}
                <Tooltip title={copied ? "Copied!" : "Copy commands"} placement="top">
                    <Box
                        onClick={handleCopy}
                        sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: copied ? BRAND.goldBright : BRAND.muted,
                            fontSize: 12,
                            transition: "color 0.2s",
                            "&:hover": { color: BRAND.gold },
                        }}
                    >
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>

            {/* ── Command lines ── */}
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {/* Optional grey comment line above the command */}
                        {comment && (
                            <Typography
                                sx={{
                                    fontFamily: "monospace",
                                    fontSize: 12,
                                    color: "rgba(255,255,255,0.3)",
                                    mb: 0.3,
                                }}
                            >
                                {/* Hash prefix marks it as a shell comment */}
                                # {comment}
                            </Typography>
                        )}

                        {/* The actual command in gold */}
                        <Typography
                            sx={{
                                fontFamily: "monospace",
                                fontSize: { xs: 13, md: 14 },
                                color: BRAND.codeText,
                            }}
                        >
                            {cmd}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ExpandableExample
//  An accordion-style card for deeper examples.
//  Props:
//    title   – accordion label
//    icon    – MUI icon
//    children – expanded content (JSX)
// ============================================================
function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);

    return (
        <Box
            sx={{
                border: `1px solid ${open ? BRAND.borderHover : BRAND.border}`,
                borderRadius: 2.5,
                overflow: "hidden",
                mb: 1.5,
                transition: "border-color 0.25s",
            }}
        >
            {/* ── Clickable header ── */}
            <Box
                onClick={() => setOpen((prev) => !prev)}
                sx={{
                    px: 2.5,
                    py: 1.8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    background: open ? BRAND.goldGlow : BRAND.surface,
                    transition: "background 0.25s",
                    "&:hover": { background: BRAND.goldGlow },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                    <Typography sx={{ color: BRAND.white, fontWeight: 600, fontSize: 14 }}>
                        {title}
                    </Typography>
                </Box>
                {/* Chevron rotates when open */}
                {open ? (
                    <ExpandLessIcon sx={{ color: BRAND.gold }} />
                ) : (
                    <ExpandMoreIcon sx={{ color: BRAND.muted }} />
                )}
            </Box>

            {/* ── Collapsible body ── */}
            <Collapse in={open}>
                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        background: "rgba(0,0,0,0.25)",
                        borderTop: `1px solid ${BRAND.border}`,
                    }}
                >
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ResourceLink
//  A styled external-link row.
//  Props:
//    href  – URL string
//    label – display text
//    sub   – short description
// ============================================================
function ResourceLink({ href, label, sub }) {
    return (
        <Box
            component="a"
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                mb: 1.2,
                textDecoration: "none",
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                background: BRAND.surface,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 16px ${BRAND.goldGlow}`,
                },
            }}
        >
            {/* Link icon */}
            <LinkIcon sx={{ color: BRAND.gold, fontSize: 20, flexShrink: 0 }} />

            <Box>
                <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>
                    {label}
                </Typography>
                {sub && (
                    <Typography sx={{ color: BRAND.muted, fontSize: 12, mt: 0.3 }}>
                        {sub}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

// ============================================================
//  MAIN COMPONENT: LinuxIntro
//  The full "Introduction to Linux" lesson page.
// ============================================================
export default function LinuxIntro() {
    return (
        // ── Page wrapper: full-height, brand background ──────
        <Box
            sx={{
                minHeight: "100vh",
                background: BRAND.bg,
                // Subtle diagonal lines texture for depth
                backgroundImage: `repeating-linear-gradient(
          -45deg,
          rgba(218,165,32,0.02) 0px,
          rgba(218,165,32,0.02) 1px,
          transparent 1px,
          transparent 12px
        )`,
                py: { xs: 4, md: 7 },
                px: { xs: 2, sm: 3 },
            }}
        >
            <Container maxWidth="lg">

                {/* ══════════════════════════════════════════════
            HERO HEADER
            Shows the lesson title, badge, and a brief
            one-liner about what the student will learn.
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>

                    {/* "Lesson" chip badge */}
                    <Chip
                        icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />}
                        label="Foundation · Module 01"
                        sx={{
                            mb: 2.5,
                            background: BRAND.goldGlow,
                            border: `1px solid ${BRAND.border}`,
                            color: BRAND.gold,
                            fontFamily: "monospace",
                            fontSize: 11,
                            letterSpacing: 1,
                            textTransform: "uppercase",
                        }}
                    />

                    {/* Main lesson title */}
                    <Typography
                        variant="h3"
                        sx={{
                            ...sectionHeading,
                            fontSize: { xs: 28, sm: 36, md: 44 },
                            mb: 2,
                            // Gold gradient on the word "Linux"
                            "& span": {
                                background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldBright})`,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            },
                        }}
                    >
                        Introduction to <span>Linux</span>
                    </Typography>

                    {/* One-liner description */}
                    <Typography
                        sx={{
                            color: BRAND.muted,
                            fontSize: { xs: 14, md: 16 },
                            maxWidth: 620,
                            lineHeight: 1.75,
                            mx: { xs: "auto", md: 0 },
                        }}
                    >
                        Linux is a free, open-source operating system kernel that powers everything
                        from Android smartphones and Raspberry Pis to the world's fastest
                        supercomputers and cloud servers. Understanding it is a career superpower.
                    </Typography>

                    {/* Quick-stats row */}
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            mt: 3,
                            justifyContent: { xs: "center", md: "flex-start" },
                        }}
                    >
                        {[
                            { label: "~3 hours", sub: "read time" },
                            { label: "Beginner", sub: "level" },
                            { label: "8 topics", sub: "covered" },
                        ].map(({ label, sub }) => (
                            <Box
                                key={label}
                                sx={{
                                    textAlign: "center",
                                    background: BRAND.surface,
                                    border: `1px solid ${BRAND.border}`,
                                    borderRadius: 2,
                                    px: 2.5,
                                    py: 1.2,
                                }}
                            >
                                <Typography sx={{ color: BRAND.goldBright, fontWeight: 800, fontSize: 16 }}>
                                    {label}
                                </Typography>
                                <Typography sx={{ color: BRAND.muted, fontSize: 11 }}>{sub}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ══════════════════════════════════════════════
            SECTION 1 – WHAT IS LINUX?
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LightbulbIcon />} label="What Is Linux?" />

                    <Typography sx={{ color: BRAND.muted, lineHeight: 1.8, mb: 2, fontSize: { xs: 13, md: 15 } }}>
                        Linux is not a single operating system — it is an <strong style={{ color: BRAND.white }}>open-source kernel</strong> first
                        released by Linus Torvalds in 1991. A kernel is the core program that manages
                        communication between hardware and software. Distributions (distros) bundle the
                        kernel with tools, package managers, and desktop environments to create
                        complete operating systems like Ubuntu, Fedora, Arch, and Debian.
                    </Typography>

                    <Typography sx={{ color: BRAND.muted, lineHeight: 1.8, fontSize: { xs: 13, md: 15 } }}>
                        Linux dominates the server market (~96% of the top 1 million web servers run Linux),
                        powers all Android devices, and is the only OS approved for use on the
                        International Space Station. It is the foundational skill for DevOps, cloud
                        engineering, cybersecurity, and backend development.
                    </Typography>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 2 – KEY CONCEPTS
            Four concept cards in a responsive 2-column grid.
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<MenuBookIcon />} label="Key Concepts" />

                    {/* Grid: 1 column on mobile, 2 on tablet+ */}
                    <Grid container spacing={2}>
                        {[
                            {
                                icon: <LayersIcon />,
                                title: "Kernel vs. Distribution",
                                description:
                                    "The kernel is the core engine. A distribution (distro) wraps the kernel with apps and tools. Ubuntu, Fedora, and Arch are all distros — all share the Linux kernel.",
                            },
                            {
                                icon: <TerminalIcon />,
                                title: "Command Line Interface (CLI)",
                                description:
                                    "The terminal lets you control Linux with typed commands. It is faster, more powerful, and more scriptable than any GUI. Every Linux professional lives here.",
                            },
                            {
                                icon: <FolderOpenIcon />,
                                title: "File System Hierarchy",
                                description:
                                    "Linux organises everything under a single root /. Key directories: /home (user files), /etc (config), /var (logs), /bin (core binaries), /tmp (temp data).",
                            },
                            {
                                icon: <BuildIcon />,
                                title: "Package Managers",
                                description:
                                    "Package managers install, update, and remove software automatically. Ubuntu uses apt, Fedora uses dnf, Arch uses pacman. Think of them as the App Store for Linux.",
                            },
                            {
                                icon: <StorageIcon />,
                                title: "Permissions & Ownership",
                                description:
                                    "Every file has an owner and permission bits (read, write, execute) for owner, group, and others. chmod and chown are the two commands you'll use most.",
                            },
                            {
                                icon: <CodeIcon />,
                                title: "Shell & Scripting",
                                description:
                                    "The shell (bash, zsh) interprets your commands. You can chain commands into shell scripts (.sh files) to automate repetitive tasks — a superpower for any developer.",
                            },
                        ].map((card) => (
                            <Grid item xs={12} sm={6} md={4} key={card.title}>
                                <ConceptCard {...card} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 3 – TRY IT YOURSELF
            Shows terminal commands with copy functionality
            and expandable deeper examples.
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="Try It Yourself" />

                    <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 3, lineHeight: 1.7 }}>
                        Open your terminal (or use an online playground like{" "}
                        <Box
                            component="a"
                            href="https://replit.com"
                            target="_blank"
                            rel="noreferrer"
                            sx={{ color: BRAND.gold, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                        >
                            Replit
                        </Box>
                        ) and run these commands one at a time. Read the output carefully — that's how
                        Linux teaches you.
                    </Typography>

                    {/* ── Basic orientation commands ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Basic Orientation
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ uname -a",  comment: "Print full kernel and system information" },
                            { cmd: "$ whoami",    comment: "Print the current logged-in username" },
                            { cmd: "$ pwd",       comment: "Print Working Directory — where you are right now" },
                            { cmd: "$ hostname",  comment: "Print the name of this machine on the network" },
                        ]}
                    />

                    {/* ── File navigation commands ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Navigating Files
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ ls -la",        comment: "List all files including hidden ones with details" },
                            { cmd: "$ cd /home",      comment: "Change directory to /home" },
                            { cmd: "$ mkdir projects",comment: "Create a new directory called 'projects'" },
                            { cmd: "$ touch hello.txt",comment: "Create an empty file called hello.txt" },
                            { cmd: "$ cat hello.txt", comment: "Print contents of hello.txt to the terminal" },
                        ]}
                    />

                    {/* ── Permissions commands ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Permissions
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ ls -l hello.txt",          comment: "See the permission bits for a file" },
                            { cmd: "$ chmod 755 hello.txt",       comment: "Give owner full access; others can read+execute" },
                            { cmd: "$ chown $USER:$USER hello.txt",comment: "Set yourself as owner and group" },
                        ]}
                    />

                    {/* ── Expandable deeper examples ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 2, mt: 4 }}>
                        🔽 Deeper Examples (click to expand)
                    </Typography>

                    {/* Accordion 1: Package management */}
                    <ExpandableExample title="Installing software with apt (Ubuntu/Debian)" icon={<BuildIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            <code style={{ color: BRAND.goldBright }}>apt</code> is Ubuntu's package manager.
                            Always run <code style={{ color: BRAND.goldBright }}>sudo apt update</code> first
                            to refresh the package list before installing anything.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo apt update",           comment: "Refresh package index from repositories" },
                                { cmd: "$ sudo apt upgrade -y",        comment: "Upgrade all installed packages" },
                                { cmd: "$ sudo apt install curl -y",   comment: "Install the curl tool" },
                                { cmd: "$ sudo apt remove curl",       comment: "Remove curl" },
                                { cmd: "$ apt search nginx",           comment: "Search for packages matching 'nginx'" },
                            ]}
                        />
                    </ExpandableExample>

                    {/* Accordion 2: Processes */}
                    <ExpandableExample title="Managing processes" icon={<StorageIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            Every running program is a <strong style={{ color: BRAND.white }}>process</strong> with
                            a unique PID (Process ID). You can list, monitor, and kill processes from the terminal.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ ps aux",            comment: "List all running processes with details" },
                                { cmd: "$ top",               comment: "Interactive real-time process monitor (q to quit)" },
                                { cmd: "$ htop",              comment: "Better-looking top (install with: sudo apt install htop)" },
                                { cmd: "$ kill -9 1234",      comment: "Forcefully kill process with PID 1234" },
                                { cmd: "$ killall firefox",   comment: "Kill all processes named 'firefox'" },
                            ]}
                        />
                    </ExpandableExample>

                    {/* Accordion 3: Shell scripting */}
                    <ExpandableExample title="Your first shell script" icon={<CodeIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            Shell scripts are plain text files that run a sequence of commands.
                            The first line (<code style={{ color: BRAND.goldBright }}>#!/bin/bash</code>) tells the OS
                            which interpreter to use. This is called a <strong style={{ color: BRAND.white }}>shebang</strong>.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ nano hello.sh",             comment: "Open the nano editor to create a script" },
                            ]}
                        />
                        <Box
                            sx={{
                                background: BRAND.codeBg,
                                border: `1px solid ${BRAND.border}`,
                                borderRadius: 2,
                                p: 2,
                                mb: 2,
                                fontFamily: "monospace",
                                fontSize: 13,
                            }}
                        >
                            {/* Show the script content visually */}
                            {[
                                "#!/bin/bash",
                                "",
                                "# This is a comment — bash ignores lines starting with #",
                                'NAME="World"',
                                'echo "Hello, $NAME!"',
                                "echo \"Today is: $(date)\"",
                            ].map((line, i) => (
                                <Typography key={i} sx={{ fontFamily: "monospace", fontSize: 13, color: line.startsWith("#") ? "rgba(255,255,255,0.35)" : BRAND.codeText, lineHeight: 1.8 }}>
                                    {line || "\u00A0" /* non-breaking space for blank lines */}
                                </Typography>
                            ))}
                        </Box>
                        <CodeBlock
                            lines={[
                                { cmd: "$ chmod +x hello.sh",  comment: "Make the script executable" },
                                { cmd: "$ ./hello.sh",          comment: "Run the script" },
                            ]}
                        />
                    </ExpandableExample>

                    {/* Accordion 4: Networking */}
                    <ExpandableExample title="Basic networking commands" icon={<LayersIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            These commands help you diagnose connectivity issues and inspect your network interfaces.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ ip a",                      comment: "Show all network interfaces and IP addresses" },
                                { cmd: "$ ping -c 4 google.com",      comment: "Send 4 ICMP packets to google.com to test connectivity" },
                                { cmd: "$ curl -I https://example.com",comment: "Fetch HTTP headers from a URL" },
                                { cmd: "$ netstat -tulnp",             comment: "List all listening ports and their processes" },
                                { cmd: "$ ss -tulnp",                  comment: "Newer, faster alternative to netstat" },
                            ]}
                        />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 4 – WHAT YOU HAVE LEARNED
            A summary checklist of this lesson's outcomes.
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="What You've Learned" />

                    <Grid container spacing={1.5}>
                        {[
                            "What Linux is and why it dominates the server world",
                            "The difference between the kernel and a distribution",
                            "How to navigate the file system using cd, ls, and pwd",
                            "How file permissions work and how to change them",
                            "How to install and remove software with apt",
                            "How to list and kill running processes",
                            "How to write and execute a basic shell script",
                            "Essential networking commands for diagnosing connectivity",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1, flexShrink: 0 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.6 }}>
                                        {item}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 5 – RESOURCES
            External links for further study.
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 4 }}>
                    <SectionTitle icon={<LinkIcon />} label="Further Resources" />

                    <ResourceLink
                        href="https://linuxjourney.com/"
                        label="Linux Journey"
                        sub="Free, interactive course covering everything from the basics to advanced topics."
                    />
                    <ResourceLink
                        href="https://ubuntu.com/tutorials/command-line-for-beginners"
                        label="Ubuntu CLI Guide"
                        sub="Official Ubuntu tutorial — ideal for absolute beginners on the command line."
                    />
                    <ResourceLink
                        href="https://www.gnu.org/software/bash/manual/"
                        label="GNU Bash Manual"
                        sub="The complete reference for the Bash shell. Bookmark this — you'll return often."
                    />
                    <ResourceLink
                        href="https://tldr.sh/"
                        label="TLDR Pages"
                        sub="Community-maintained, simplified man pages. Run 'tldr ls' and get straight to the point."
                    />
                    <ResourceLink
                        href="https://explainshell.com/"
                        label="ExplainShell"
                        sub="Paste any shell command and get a visual breakdown of every flag and argument."
                    />
                </Box>

                {/* ── Footer note ── */}
                <Box
                    sx={{
                        mt: 2,
                        p: 2.5,
                        border: `1px solid ${BRAND.border}`,
                        borderRadius: 2,
                        background: BRAND.goldGlow,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                    }}
                >
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.65 }}>
                        <strong style={{ color: BRAND.white }}>Pro tip for juniors:</strong> Don't try to
                        memorise commands. Focus on understanding what each one does and why. Use{" "}
                        <code style={{ color: BRAND.goldBright }}>man &lt;command&gt;</code> to read the
                        built-in manual for any command, or{" "}
                        <code style={{ color: BRAND.goldBright }}>command --help</code> for a quick summary.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
